<template>
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          Recent Activity (Past 30 Days)
        </template>
        <template #actions>
          <div class="actions-container">
            <a @click="refresh" title="Refresh" tabindex="0" @keypress="refresh" role="button">
              <font-awesome-icon :icon="['fas', 'sync-alt']" size="lg" />
            </a>
            <router-link
              :to="{ path: matchHistoryRoute }"
              title="View Details"
            >
              <font-awesome-icon
                :icon="['far', 'angle-double-right']"
                size="2x"
              />
            </router-link>
          </div>
        </template>
      </es-header>
    </div>
    <div class="tabs-container no-border-top">
      <div class="tabs" ref="tabs" @keydown="onKeydown">
        <ul>
          <li :class="{ 'is-active': isBatches, hovered: hoverIdx === 0 }">
            <a
              @click="updateType('batches')"
              @blur="onBlur()"
              @focus="hoverIdx = 0"
              role="button"
              :tabindex="isBatches ? 0 : -1"
            >
              BATCHES
            </a>
          </li>
          <li :class="{ 'is-active': isTransactions, hovered: hoverIdx === 1 }">
            <a
              @click="updateType('transactions')"
              @blur="onBlur()"
              @focus="hoverIdx = 1"
              role="button"
              :tabindex="isTransactions ? 0 : -1"
            >
              TRANSACTIONS
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div style="padding: 1.5rem 1.25rem .5rem 2rem">
      <div class="is-flex flex-justify--space-between" style="margin-bottom: 0.5rem">
        <div class="has-text-weight-bold has-text-left">
          Pending - 5 Most Recent
        </div>
        <div class="is-flex drillin-controls has-text-primary has-text-weight-bold">
          <a class="padding-left--small padding-right--small border-right" style="font-size:12px">
            <router-link :to="{
                path: matchHistoryRoute,
                query: buildQuery(6, 'pending'),
              }">
              View Past 7 Days
            </router-link>
          </a>
          <a class="padding-left--small" style="font-size:12px">
            <router-link :to="{
                path: matchHistoryRoute,
                query: buildQuery(29, 'pending'),
              }">
              View All
              <font-awesome-icon :icon="['far', 'angle-double-right']" style="font-size: 12px;" />
            </router-link>
          </a>
        </div>
      </div>
      <div style="margin-bottom: 1rem">
        <pid-batches-table
          v-if="isBatches"
          :batches="batchesPending"
          :loading="loadingPendingBatches"
          :loadingError="errorPending"
        />
        <pid-transactions-table
          v-else-if="isTransactions"
          :transactions="transactionPending"
          :loading="loadingPendingTransactions"
          :loadingError="errorPending"
        />
      </div>
      <div class="is-flex flex-justify--space-between" style="margin-bottom: 0.5rem">
        <div class="has-text-weight-bold has-text-left">
          Completed - 5 Most Recent
        </div>
        <div class="is-flex has-text-primary has-text-weight-bold">
          <a class="padding-left--small padding-right--small border-right" style="font-size:12px">
            <router-link :to="{
                path: matchHistoryRoute,
                query: buildQuery(6, 'completed'),
              }">
              View Past 7 Days
            </router-link>
          </a>
          <a class="padding-left--small" style="font-size:12px">
            <router-link :to="{
                path: matchHistoryRoute,
                query: buildQuery(29, 'completed'),
              }">
              View All
              <font-awesome-icon :icon="['far', 'angle-double-right']" style="font-size: 12px;" />
            </router-link>
          </a>
        </div>
      </div>
      <pid-batches-table
        v-if="isBatches"
        :batches="batchesCompleted"
        :loading="loadingCompletedBatches"
        :loadingError="errorCompleted"
      />
      <pid-transactions-table
        v-else-if="isTransactions"
        :transactions="transactionCompleted"
        :loading="loadingCompletedTransactions"
        :loadingError="errorCompleted"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faSyncAlt,
} from '@fortawesome/pro-solid-svg-icons';
import EsHeader from '@/components/EsHeader.vue';
import PidBatchesTable from '@/components/PidBatchesTable.vue';
import PidTransactionsTable from '@/components/PidTransactionsTable.vue';
import DateUtils from '@/mixins/DateUtils';

library.add(
  faAngleDoubleRight,
  faSyncAlt,
);

export default {
  mixins: [
    DateUtils,
  ],
  components: {
    EsHeader,
    PidBatchesTable,
    PidTransactionsTable,
  },
  watch: {
    type() {
      this.refresh();
    },
  },
  data() {
    return {
      batchesCompleted: [],
      batchesPending: [],
      errorCompleted: null,
      errorPending: null,
      loadingCompletedBatches: null,
      loadingCompletedTransactions: null,
      loadingPendingBatches: null,
      loadingPendingTransactions: null,
      pendingStatuses: [],
      transactionCompleted: [],
      transactionPending: [],
      type: null,
      hoverIdx: -1,
      selectedIndex: -1,
    };
  },
  created() {
    this.type = 'batches';
    this.pendingStatuses = [
      { description: 'COMPLETED_WITH_PENDING', inTransactions: true },
      { description: 'ERRORED', inTransactions: true },
      { description: 'ERRORED_VALIDATION', inTransactions: true },
      { description: 'ERRORED_MATCHING', inTransactions: true },
      { description: 'VALIDATION_IN_PROGRESS', inTransactions: false },
      { description: 'VALIDATION_COMPLETE', inTransactions: false },
      { description: 'RUNNING_MATCHING', inTransactions: false },
      { description: 'LOADED', inTransactions: false },
      { description: 'QUEUED', inTransactions: false },
    ];
  },
  computed: {
    batchesPendingStatuses() {
      return this.pendingStatuses.map(status => status.description);
    },
    isBatches() {
      return this.type === 'batches';
    },
    isTransactions() {
      return this.type === 'transactions';
    },
    matchHistoryRoute() {
      return `matchHistory/${this.isBatches ? 'batches' : 'transactions'}`;
    },
    transactionsPendingStatuses() {
      return this.pendingStatuses
        .filter(status => status.inTransactions)
        .map(status => status.description);
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPidTransactionStatuses',
    ]),
    buildQuery(days, status) {
      const query = {};
      if (status === 'pending') {
        query.status = this.isBatches
          ? this.batchesPendingStatuses
          : ['PENDING'];
      } else if (status === 'completed') {
        query.status = this.isBatches
          ? ['COMPLETED', 'VALIDATION_CANCELED']
          : ['COMPLETED', 'CANCELED'];
      }
      query.startDate = this.buildDateString(days);

      return query;
    },
    async completedBatches() {
      try {
        this.loadingCompletedBatches = true;
        this.errorCompleted = false;
        const sort = [{
          field: 'id',
          order: 'DESC',
        }];
        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'false');
        params.append('sort', JSON.stringify(sort));
        params.append('dashboard', 'true');
        params.append('startDate', moment().subtract(30, 'days').format('YYYY-MM-DD'));

        const res = await axios.get('personid/batches', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });
        this.batchesCompleted = res.data.data;
      } catch (e) {
        this.errorCompleted = true;
      } finally {
        this.loadingCompletedBatches = false;
      }
    },
    async completedTransactions() {
      try {
        this.loadingCompletedTransactions = true;
        this.errorCompleted = false;
        const sort = [{
          field: 'id',
          order: 'DESC',
        }];
        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'false');
        params.append('sort', JSON.stringify(sort));
        params.append('startDate', moment().subtract(30, 'days').format('YYYY-MM-DD'));
        const res = await axios.get('personid/transactions', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });
        this.transactionCompleted = res.data.data;
      } catch (e) {
        this.errorCompleted = true;
      } finally {
        this.loadingCompletedTransactions = false;
      }
    },
    async getBatches() {
      this.loadingPendingBatches = true;
      this.loadingCompletedBatches = true;
      await this.loadPidTransactionStatuses();
      await Promise.all([
        this.pendingBatches(),
        this.completedBatches(),
      ]);
    },
    async getTransactions() {
      this.loadingPendingTransactions = true;
      this.loadingCompletedTransactions = true;
      await this.loadPidTransactionStatuses();
      await Promise.all([
        this.completedTransactions(),
        this.pendingTransactions(),
      ]);
    },
    async pendingBatches() {
      try {
        this.loadingPendingBatches = true;
        this.errorPending = false;
        const sort = [{
          field: 'id',
          order: 'DESC',
        }];
        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'true');
        params.append('sort', JSON.stringify(sort));
        params.append('dashboard', 'true');
        params.append('startDate', moment().subtract(30, 'days').format('YYYY-MM-DD'));
        const res = await axios.get('personid/batches', {
          params,
        });
        this.batchesPending = res.data.data;
      } catch (e) {
        this.errorPending = true;
      } finally {
        this.loadingPendingBatches = false;
      }
    },
    async pendingTransactions() {
      try {
        this.loadingPendingTransactions = true;
        this.errorPending = false;
        const sort = [{
          field: 'id',
          order: 'DESC',
        }];
        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'true');
        params.append('startDate', moment().subtract(30, 'days').format('YYYY-MM-DD'));
        params.append('sort', JSON.stringify(sort));
        const res = await axios.get('personid/transactions', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });
        this.transactionPending = res.data.data;
      } catch (e) {
        this.errorPending = true;
      } finally {
        this.loadingPendingTransactions = false;
      }
    },
    refresh() {
      if (this.isBatches) {
        this.transactionPending = [];
        this.transactionCompleted = [];
        this.getBatches();
      } else if (this.isTransactions) {
        this.batchesPending = [];
        this.batchesCompleted = [];
        this.getTransactions();
      }
    },
    updateType(type) {
      this.type = type;
      this.selectedIndex = type === 'batches' ? 0 : 1;
      this.hoverIdx = this.selectedIndex;
    },
    focusOption(index) {
      this.selectedIndex = index;
      this.hoverIdx = index;
      this.$refs.tabs.children[0].children[index].children[0].focus();
    },
    onBlur() {
      this.hoverIdx = -1;
    },
    onKeydown(evt) {
      const total = 2; // two tabs: BATCHES and TRANSACTIONS

      if (evt.key === 'ArrowRight') {
        evt.preventDefault();
        this.hoverIdx = (this.hoverIdx + 1) % total;
      }

      if (evt.key === 'ArrowLeft') {
        evt.preventDefault();
        this.hoverIdx = (this.hoverIdx - 1 + total) % total;
      }

      if (evt.key === 'Enter' || evt.key === ' ') {
        evt.preventDefault();
        this.focusOption(this.hoverIdx);
        this.updateType(this.hoverIdx === 0 ? 'batches' : 'transactions');
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .padding-left--small
    padding-left: 0.5rem
  .padding-right--small
    padding-right: 0.5rem
  .border-right
    border-right: 1px solid #ccc
  .actions-container
    display: flex;
    justify-content: right;
    align-items: center;
    height: 100%;
    & a
      height: fit-content
      &:not(:last-child)
        margin-right: 1rem
  .hovered
    text-shadow: #444444 0px 0px 0.65px, #444444 0px 0px 0.65px
    a
      color: #444
      border-bottom-color: #444444
</style>
